import { useState, useMemo, useEffect } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useNavigate, useParams, Link } from "react-router-dom";
import { Plus, Search, MessageSquare, Hash, Bot, Users } from "lucide-react";
import { GrupoAvatar } from "../../components/GrupoAvatar";
import { listarConversas, ConversaResumo, abrirOuCriarDM } from "../../services/chat";
import { listarPessoas, PessoaOnline } from "../../services/presence";
import { useAuth } from "../../hooks/useAuth";
import { ChatThread } from "./ChatThread";
import { NovaConversaModal } from "./NovaConversaModal";
import { Avatar } from "../../components/Avatar";
import { ChatContextMenu } from "../../components/ChatContextMenu";

type Aba = "tudo" | "grupos" | "pessoas";

const TABS: { key: Aba; label: string; icon: any }[] = [
  { key: "tudo", label: "Tudo", icon: MessageSquare },
  { key: "grupos", label: "Grupos", icon: Hash },
  { key: "pessoas", label: "Pessoas", icon: Users },
];

export function ChatPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { conversaId } = useParams<{ conversaId?: string }>();
  const [aba, setAba] = useState<Aba>("tudo");
  const [busca, setBusca] = useState("");
  const [showNova, setShowNova] = useState(false);
  const [menuCtx, setMenuCtx] = useState<{ conversa: ConversaResumo; x: number; y: number } | null>(null);

  const { data: conversas = [] } = useQuery({
    queryKey: ["chat-conversas"],
    queryFn: listarConversas,
    refetchInterval: 60000,
  });

  // Auto-seleciona primeira conversa em desktop quando nada selecionado
  useEffect(() => {
    if (!conversaId && conversas.length > 0 && window.innerWidth >= 768) {
      navigate(`/chat/${conversas[0].id}`, { replace: true });
    }
  }, [conversaId, conversas, navigate]);

  const filtradas = useMemo(() => {
    let lista = conversas.filter((c) => c.tipo !== "projeto");
    if (aba === "grupos") lista = lista.filter((c) => c.tipo === "grupo");
    if (busca.trim()) {
      const t = busca.trim().toLowerCase();
      lista = lista.filter((c) => tituloConversa(c, user?.id).toLowerCase().includes(t));
    }
    return lista;
  }, [conversas, aba, busca, user?.id]);

  const totalNaoLidas = conversas.reduce((s, c) => s + (c.naoLidas || 0), 0);

  return (
    <div className="chat-scope flex h-[calc(100vh-9rem)] sm:h-[calc(100vh-7rem)] -m-4 sm:-m-6 lg:-m-8 bg-slate-50 min-h-0">
      {/* Sidebar (lista) */}
      <aside
        className={`${conversaId ? "hidden md:flex" : "flex"} flex-col w-full md:w-80 lg:w-96 border-r border-slate-200 bg-white shrink-0`}
      >
        <div className="p-3 border-b border-slate-200 space-y-2">
          <div className="flex items-center justify-between">
            <h1 className="text-lg font-bold text-slate-800 flex items-center gap-2">
              <MessageSquare size={18} className="text-blue-600" /> Chat
              {totalNaoLidas > 0 && (
                <span className="bg-red-500 text-white text-[10px] font-bold rounded-full px-1.5 py-0.5">{totalNaoLidas}</span>
              )}
            </h1>
            <button
              onClick={() => setShowNova(true)}
              className="bg-blue-600 hover:bg-blue-700 text-white p-1.5 rounded-lg"
              title="Nova conversa"
            >
              <Plus size={16} />
            </button>
          </div>

          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
            <input
              type="text"
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              placeholder="Buscar conversa…"
              className="w-full pl-9 pr-3 py-2 rounded-lg border border-slate-200 text-sm outline-none focus:border-blue-500"
            />
          </div>

          <div className="flex bg-slate-100 rounded-lg p-0.5">
            {TABS.map((t) => (
              <button
                key={t.key}
                onClick={() => setAba(t.key)}
                className={`flex-1 flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-md text-xs font-medium transition-colors ${
                  aba === t.key ? "bg-white text-blue-600 shadow-sm" : "text-slate-500 hover:text-slate-700"
                }`}
              >
                <t.icon size={12} /> {t.label}
              </button>
            ))}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          {aba === "pessoas" ? (
            <PessoasLista busca={busca} userId={user?.id} />
          ) : (
            <>
              {filtradas.length === 0 && (
                <p className="text-sm text-slate-400 text-center py-8 px-4">
                  {busca ? "Nada encontrado" : "Nenhuma conversa ainda. Clique no + para começar."}
                </p>
              )}
              {filtradas.map((c) => (
                <ConversaItem
                  key={c.id}
                  c={c}
                  ativa={c.id === conversaId}
                  userId={user?.id}
                  onContextMenu={(e) => {
                    e.preventDefault();
                    setMenuCtx({ conversa: c, x: e.clientX, y: e.clientY });
                  }}
                />
              ))}
            </>
          )}
        </div>
      </aside>

      {/* Thread */}
      {conversaId ? (
        <ChatThread conversaId={conversaId} onVoltar={() => navigate("/chat")} />
      ) : (
        <div className="hidden md:flex flex-1 flex-col items-center justify-center text-slate-400 p-6 text-center">
          <MessageSquare size={48} className="mx-auto mb-2 text-slate-300" />
          <p className="text-sm">Selecione uma conversa ou clique em + para começar.</p>
        </div>
      )}

      {showNova && <NovaConversaModal onClose={() => setShowNova(false)} />}
      {menuCtx && (
        <ChatContextMenu
          conversa={menuCtx.conversa}
          userId={user?.id}
          x={menuCtx.x}
          y={menuCtx.y}
          onClose={() => setMenuCtx(null)}
        />
      )}
    </div>
  );
}

function ConversaItem({ c, ativa, userId, onContextMenu }: {
  c: ConversaResumo;
  ativa: boolean;
  userId?: string;
  onContextMenu?: (e: React.MouseEvent) => void;
}) {
  const titulo = tituloConversa(c, userId);
  const ultima = c.mensagens?.[0];
  const previewBase = ultima?.anexos?.length
    ? `📎 ${ultima.anexos[0].tipo}`
    : ultima?.texto?.slice(0, 60) || "";
  const autorPrefix = ultima?.autorIa
    ? "IA: "
    : ultima?.autor?.nome && c.tipo !== "dm"
      ? `${ultima.autor.nome.split(" ")[0]}: `
      : "";
  const Icon = c.tipo === "ia" ? Bot : MessageSquare;

  return (
    <Link
      to={`/chat/${c.id}`}
      onContextMenu={onContextMenu}
      className={`block px-3 py-2.5 border-b border-slate-50 hover:bg-slate-50 transition-colors ${ativa ? "bg-blue-50/60 border-l-2 border-l-blue-600" : ""}`}
    >
      <div className="flex items-center gap-2">
        {c.tipo === "grupo" ? (
          <GrupoAvatar fotoUrl={c.fotoUrl} tamanho={36} titulo={titulo} />
        ) : (
          <div className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${
            c.tipo === "ia"
              ? "bg-gradient-to-br from-blue-500 to-blue-700 text-white"
              : "bg-blue-100 text-blue-700"
          }`}>
            <Icon size={16} />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <p className="text-sm font-medium text-slate-800 truncate flex-1">{titulo}</p>
            {ultima?.criadoEm && (
              <span className="text-[10px] text-slate-400 shrink-0">{formatarHora(ultima.criadoEm)}</span>
            )}
          </div>
          <div className="flex items-center gap-2 mt-0.5">
            <p className="text-xs text-slate-500 truncate flex-1">
              {autorPrefix}{previewBase || <span className="italic">sem mensagens</span>}
            </p>
            {(c.naoLidas || 0) > 0 && (
              <span className="bg-blue-600 text-white text-[10px] font-bold rounded-full px-1.5 shrink-0">{c.naoLidas}</span>
            )}
          </div>
        </div>
      </div>
    </Link>
  );
}

export function tituloConversa(c: ConversaResumo, userId?: string): string {
  if (c.tipo === "ia") return "IA";
  if (c.tipo === "projeto") return c.projeto?.nome || c.nome || "Projeto";
  if (c.tipo === "grupo") return c.nome || "Grupo";
  // DM: mostra o nome do outro
  const outro = c.participantes.find((p) => p.usuarioId !== userId);
  return outro?.usuario.nome || "Conversa";
}

function formatarHora(iso: string) {
  const d = new Date(iso);
  const hoje = new Date();
  if (d.toDateString() === hoje.toDateString()) {
    return d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
  }
  return d.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" });
}

function PessoasLista({ busca, userId }: { busca: string; userId?: string }) {
  const navigate = useNavigate();
  const { data: pessoas = [], isLoading } = useQuery({
    queryKey: ["presence"],
    queryFn: listarPessoas,
    refetchInterval: 30000,
  });

  const dmM = useMutation({
    mutationFn: (id: string) => abrirOuCriarDM(id),
    onSuccess: (c) => navigate(`/chat/${c.id}`),
  });

  const filtradas = useMemo(() => {
    const t = busca.trim().toLowerCase();
    return pessoas
      .filter((p) => p.id !== userId)
      .filter((p) => !t || p.nome.toLowerCase().includes(t) || p.email.toLowerCase().includes(t));
  }, [pessoas, busca, userId]);

  const online = filtradas.filter((p) => p.online);
  const offline = filtradas.filter((p) => !p.online);

  return (
    <div>
      {isLoading && <p className="text-sm text-slate-400 text-center py-8">Carregando…</p>}

      {online.length > 0 && (
        <>
          <p className="px-3 py-1.5 text-[10px] uppercase font-bold text-emerald-700 bg-emerald-50">Online · {online.length}</p>
          {online.map((p) => <PessoaItem key={p.id} p={p} onClick={() => dmM.mutate(p.id)} />)}
        </>
      )}
      {offline.length > 0 && (
        <>
          <p className="px-3 py-1.5 text-[10px] uppercase font-bold text-slate-500 bg-slate-50">Offline · {offline.length}</p>
          {offline.map((p) => <PessoaItem key={p.id} p={p} onClick={() => dmM.mutate(p.id)} />)}
        </>
      )}
      {!isLoading && filtradas.length === 0 && (
        <p className="text-sm text-slate-400 text-center py-8 px-4">Ninguém encontrado</p>
      )}
    </div>
  );
}

function PessoaItem({ p, onClick }: { p: PessoaOnline; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className="w-full text-left px-3 py-2 hover:bg-slate-50 flex items-center gap-3 border-b border-slate-50"
    >
      <div className="relative shrink-0">
        <Avatar nome={p.nome} fotoUrl={p.fotoUrl} tamanho={36} bgFallback="bg-blue-100" textFallback="text-blue-700" />
        <span className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-white ${p.online ? "bg-emerald-500" : "bg-slate-300"}`} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-slate-800 truncate">{p.nome}</p>
        <p className="text-[11px] text-slate-500 truncate">{p.email} · {p.perfil}</p>
      </div>
    </button>
  );
}

// avoid unused warning
void Bot;
